import { AlertTriangle, Crown, Download, Send, X } from "lucide-react";
import { useEffect } from "react";
import posthog from "posthog-js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import type { GenerationQualityVariant } from "../lib/generationQuality";

interface WatermarkConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  onUpgrade: () => void;
  action: "publish" | "export";
  /** When true (export only), there is no free download — the dialog offers upgrade or free publish instead. */
  gated?: boolean;
  /** Called when the user picks the free "publish instead" option in gated mode. */
  onPublishInstead?: () => void;
  /** The behavior that generated the post being exported. */
  generationQualityVariant?: GenerationQualityVariant;
  postId?: string;
}

export function WatermarkConfirmDialog({
  isOpen,
  onClose,
  onProceed,
  onUpgrade,
  action,
  gated = false,
  onPublishInstead,
  generationQualityVariant,
  postId,
}: WatermarkConfirmDialogProps) {
  const actionText = action === "publish" ? "published" : "exported";
  const actionVerb = action === "publish" ? "Publish" : "Export";
  const isGatedExport = gated && action === "export";

  useEffect(() => {
    if (isOpen && isGatedExport) {
      posthog.capture("export_gate_dialog_shown", {
        generation_quality_variant: generationQualityVariant,
        postId,
      });
    }
  }, [generationQualityVariant, isOpen, isGatedExport, postId]);

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md">
        {/* Close button in top right */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 h-6 w-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>

        {isGatedExport ? (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF5C35]/10 text-[#FF5C35]">
                  <Download className="h-5 w-5" />
                </div>
                <AlertDialogTitle>Exporting is a paid feature</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="pt-3">
                Upgrade to download your post as full-quality files with no
                watermark — on any paid plan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col items-stretch gap-2 pt-2">
              <button
                onClick={onUpgrade}
                className="flex items-center justify-center gap-2 rounded-lg bg-[#FF5C35] px-4 py-3 font-semibold text-white transition-colors hover:bg-[#FF5C35]/90"
              >
                <Crown className="h-4 w-4" />
                Upgrade to export
              </button>
              <button
                onClick={() => {
                  posthog.capture("export_gate_publish_instead_clicked", {
                    generation_quality_variant: generationQualityVariant,
                    postId,
                  });
                  onClose();
                  onPublishInstead?.();
                }}
                className="flex items-center justify-center gap-2 rounded-lg border border-input bg-background px-4 py-3 font-medium text-foreground transition-colors hover:bg-accent"
              >
                <Send className="h-4 w-4" />
                Publish or schedule — free
              </button>
              <p className="pt-0.5 text-center text-xs text-muted-foreground">
                Published posts include the “Made with WaveGen” watermark.
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-950">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <AlertDialogTitle>Watermark Will Be Applied</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="pt-3">
                Your {actionText} content will include a "Made with WaveGen"
                watermark. Upgrade to a paid plan to remove the watermark from
                all your content, including previously generated posts.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={onUpgrade}
                className="bg-primary hover:bg-primary/90"
              >
                Upgrade Plan
              </AlertDialogAction>
              <AlertDialogAction
                onClick={() => {
                  onProceed();
                  onClose();
                }}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
              >
                {actionVerb} with Watermark
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
